import type { DealCostOutcome, DealCostType } from '@/types/deal-costs';
import type { DocumentType } from '@/types/document';
import type { AmountMovedAtPosting } from '@/types/fx-period-end';
import type { ManagementFeeSettlement } from '@/types/management-fees';

export type FundTransactionType =
  | 'MANAGEMENT_FEE'
  | 'MANAGEMENT_FEE_RECOGNITION'
  | 'FUND_COST'
  | 'FUND_COST_RECOGNITION'
  | 'DEAL_COST'
  | 'DISTRIBUTION'
  | 'DRAWDOWN'
  | 'ASSET_SALE'
  | 'INVESTMENT_PURCHASE'
  | 'FINANCING_ROUND'
  | 'CONVERTIBLE_INVESTMENT'
  | 'CONVERTIBLE_CONVERSION'
  | 'CONVERTIBLE_REPAYMENT'
  | 'CURRENCY_CONVERSION'
  | 'FX_PERIOD_END'
  | 'LP_TRANSFER';

// Logical grouping of transaction types, sent as the `transaction_category`
// list filter to select every asset transaction at once.
export type FundTransactionCategory = 'ASSET';

export const FUND_TRANSACTION_TYPE_LABELS: Record<FundTransactionType, string> =
  {
    MANAGEMENT_FEE: 'Management Fee',
    MANAGEMENT_FEE_RECOGNITION: 'Management Fee Recognition',
    FUND_COST: 'Fund Cost',
    FUND_COST_RECOGNITION: 'Fund Cost Recognition',
    DEAL_COST: 'Deal Cost',
    DISTRIBUTION: 'Distribution',
    DRAWDOWN: 'Drawdown',
    ASSET_SALE: 'Asset Sale',
    INVESTMENT_PURCHASE: 'Investment Purchase',
    FINANCING_ROUND: 'Financing Round',
    CONVERTIBLE_INVESTMENT: 'Convertible Investment',
    CONVERTIBLE_CONVERSION: 'Convertible Conversion',
    CONVERTIBLE_REPAYMENT: 'Convertible Repayment',
    CURRENCY_CONVERSION: 'Currency Conversion',
    FX_PERIOD_END: 'FX Period End',
    LP_TRANSFER: 'LP Transfer',
  };

export type FundTransactionStatus =
  'DRAFT' | 'ISSUED' | 'APPROVED' | 'POSTED' | 'REVERSED';

export function isBooked(status: FundTransactionStatus): boolean {
  return status === 'APPROVED' || status === 'POSTED';
}

export type OverallStatus =
  'DRAFT_PC' | 'AWAITING_BANK_ENTRIES' | 'AWAITING_FUND_APPROVAL' | 'COMPLETE';

type ReconciliationSource = 'fund_input' | 'bank' | 'legal_doc';

type BankEntrySource = 'MANUAL' | 'PSD2';

// === Wire-format DTOs (raw from API, Decimal as string) ===

interface ApiManagementFeeSnapshot {
  schedule_id: string;
  period_start: string;
  period_end: string;
  fee_basis: string;
  fee_basis_amount: string;
  annual_rate: string;
  day_fraction: string;
  recognition_month: string | null;
  settled_through: ManagementFeeSettlement;
}

interface ApiDistributionSnapshot {
  distribution_id: string;
  distribution_number: number;
  gross_amount: string;
  total_deductions: string;
  net_amount: string;
}

export interface ApiFundTransactionSummary {
  id: string;
  fund_id: string;
  transaction_type: FundTransactionType;
  transaction_date: string;
  amount: string;
  currency: string;
  description: string | null;
  status: FundTransactionStatus;
  distribution_number: number | null;
  drawdown_id: string | null;
  captable_transaction_id: string | null;
  captable_ledger_transaction_id: string | null;
  overall_status: OverallStatus | null;
  created_at: string;
  updated_at: string;
}

export interface ApiFundTransactionDetail extends ApiFundTransactionSummary {
  currency_id: string;
  management_fee: ApiManagementFeeSnapshot | null;
  fund_cost: FundCostSnapshot | null;
  deal_cost: DealCostSnapshot | null;
  distribution: ApiDistributionSnapshot | null;
  drawdown: DrawdownSnapshot | null;
  asset_sale: AssetSaleSnapshot | null;
  financing_round: FinancingRoundSnapshot | null;
  convertible: ConvertibleSnapshot | null;
  currency_conversion: CurrencyConversionSnapshot | null;
  fx_period_end: FxPeriodEndSnapshot | null;
  lp_transfer: LpTransferSnapshot | null;
}

export interface ApiBankEntryResponse {
  id: string;
  fund_transaction_id: string;
  value_date: string;
  amount: string;
  currency_id: string;
  currency: string;
  counterparty_iban: string;
  payment_reference: string | null;
  source: BankEntrySource;
  /** Set when this entry mirrors a drawdown payment, which is where that money
   *  is listed. Null for an entry recorded against the transaction itself. */
  source_drawdown_payment_id: string | null;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
}

// === Domain types (post-mapper, monetary fields as number) ===

interface ManagementFeeSnapshot {
  schedule_id: string;
  period_start: string;
  period_end: string;
  fee_basis: string;
  fee_basis_amount: number;
  annual_rate: number;
  day_fraction: number;
  recognition_month: string | null;
  settled_through: ManagementFeeSettlement;
}

interface DistributionSnapshot {
  distribution_id: string;
  distribution_number: number;
  gross_amount: number;
  total_deductions: number;
  net_amount: number;
}

interface FundCostSnapshot {
  fund_cost_id: string;
  period_start: string;
  period_end: string;
  cost_category: string;
  recognition_month: string | null;
}

interface DealCostSnapshot {
  deal_cost_id: string;
  outcome: DealCostOutcome;
  portfolio_company_id: string | null;
  portfolio_company_name: string | null;
  cost_type: DealCostType;
}

interface DrawdownSnapshot {
  drawdown_id: string;
  drawdown_number: number;
  purpose: string;
  issue_date: string;
  due_date: string;
}

export interface AssetSaleSnapshot {
  portfolio_company_id: string;
  portfolio_company_name: string | null;
  share_class_id: string | null;
  share_class_name: string | null;
  stakeholder_id: string | null;
  buyer_input_type: string | null;
  buyer_name: string | null;
  units_sold: string | null;
  sale_amount_base: string | null;
  sale_amount_asset: string | null;
  fx_rate: string | null;
  realized_gain_loss: string | null;
  realized_fx_gain_loss: string | null;
}

export interface FinancingRoundSnapshot {
  portfolio_company_id: string;
  portfolio_company_name: string | null;
  round_name: string | null;
  pre_money_valuation: string | null;
  post_money_valuation: string | null;
  shares_acquired: string | null;
  price_per_share: string | null;
  invested_amount_base: string | null;
  invested_amount_asset: string | null;
  fx_rate: string | null;
  underlying_captable_type: string;
}

export interface ConvertibleSnapshot {
  portfolio_company_id: string;
  portfolio_company_name: string | null;
  convertible_instrument_id: string;
  instrument_type: string;
  event_kind: string;
  principal_amount: string | null;
  accrued_interest: string | null;
  shares_issued_on_conversion: string | null;
  amount_base: string | null;
  fx_rate: string | null;
}

/**
 * Both amounts are as the bank executed them. `spot_rate` is the ECB rate for
 * the day, and `settlement_spread` the gap between the two - the bank's margin.
 * Null when buying foreign currency, where the amount paid is itself the cost
 * of acquiring it.
 */
export interface CurrencyConversionSnapshot {
  from_currency: string;
  from_amount: string;
  to_currency: string;
  to_amount: string;
  bank_rate: string;
  spot_rate: string;
  settlement_spread: string | null;
}

export interface FundTransactionSummary {
  id: string;
  fund_id: string;
  transaction_type: FundTransactionType;
  transaction_date: string;
  amount: number;
  currency: string;
  description: string | null;
  status: FundTransactionStatus;
  distribution_number: number | null;
  drawdown_id: string | null;
  captable_transaction_id: string | null;
  captable_ledger_transaction_id: string | null;
  overall_status: OverallStatus | null;
  created_at: string;
  updated_at: string;
}

export interface FundTransactionDetail extends FundTransactionSummary {
  currency_id: string;
  management_fee: ManagementFeeSnapshot | null;
  fund_cost: FundCostSnapshot | null;
  deal_cost: DealCostSnapshot | null;
  distribution: DistributionSnapshot | null;
  drawdown: DrawdownSnapshot | null;
  asset_sale: AssetSaleSnapshot | null;
  financing_round: FinancingRoundSnapshot | null;
  convertible: ConvertibleSnapshot | null;
  currency_conversion: CurrencyConversionSnapshot | null;
  fx_period_end: FxPeriodEndSnapshot | null;
  lp_transfer: LpTransferSnapshot | null;
}

/**
 * Commitments are each LP's in this fund, before and after the transfer. The
 * nullable figures are null on transfers recorded before they were fund
 * transactions.
 */
export interface LpTransferSnapshot {
  transferor_investor_id: string;
  transferor_name: string;
  transferee_investor_id: string;
  transferee_name: string;
  transferor_commitment_before: string | null;
  transferor_commitment_after: string | null;
  transferee_commitment_after: string | null;
  transferred_called_amount: string | null;
  transferred_contributed_amount: string | null;
  transferred_unfunded_commitment_amount: string;
}

/**
 * What the period end came to when its draft was created, and what it posted.
 *
 * Approving recomputes from the ledger as it then stands rather than replaying
 * the draft, so the two can differ. `posted_amount` is null until approval;
 * once set it is the period end's result and `previewed_amount` is history.
 * Both are signed as account 4500's own leg reads: negative is a gain.
 */
export interface FxPeriodEndSnapshot {
  period_end_date: string;
  previewed_amount: string;
  posted_amount: string | null;
}

export interface SlotState {
  source: ReconciliationSource;
  required: boolean;
  verified: boolean;
  artefact_id: string | null;
  artefact_label: string | null;
}

interface ManualApprovalInfo {
  actor_user_id: string;
  actor_name: string | null;
  reason: string;
  approved_at: string;
}

export interface ReconciliationStateResponse {
  transaction_id: string;
  status: FundTransactionStatus;
  threshold: number;
  verified_source_count: number;
  slots: SlotState[];
  can_manual_approve: boolean;
  manual_override_allowed: boolean;
  manual_approval: ManualApprovalInfo | null;
  /** Null unless posting booked a different figure from the approved one. */
  amount_moved_at_posting: AmountMovedAtPosting | null;
}

export interface BankEntryResponse {
  id: string;
  fund_transaction_id: string;
  value_date: string;
  amount: number;
  currency_id: string;
  currency: string;
  counterparty_iban: string;
  payment_reference: string | null;
  source: BankEntrySource;
  /** Set when this entry mirrors a drawdown payment, which is where that money
   *  is listed. Null for an entry recorded against the transaction itself. */
  source_drawdown_payment_id: string | null;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
}

interface BankEntryManualFields {
  value_date: string;
  amount: string;
  currency_id: string;
  counterparty_iban: string;
  payment_reference?: string | null;
}

export interface AddBankEntryRequest {
  manual_entry: BankEntryManualFields;
}

export type UpdateBankEntryRequest = BankEntryManualFields;

export interface ManualApproveRequest {
  reason: string;
}

export interface GenerateManagementFeeDraftsResponse {
  accepted: boolean;
  message: string;
}

interface FundTransactionJournalEntryLine {
  id: string;
  account_code: string;
  account_name: string;
  debit_amount: number;
  credit_amount: number;
  currency: string;
  description: string;
  journal_entry_number: string;
}

export interface FundTransactionGLTransaction {
  id: string;
  fund_id: string;
  fund_name: string;
  transaction_type: string;
  transaction_date: string;
  total_amount: number;
  currency: string;
  description: string;
  reference: string | null;
  status: string;
  lines: FundTransactionJournalEntryLine[];
}

export interface FundTransactionDocument {
  id: string;
  fund_transaction_id: string;
  document_id: string;
  document_types: DocumentType[];
  document_name: string;
  document_original_filename: string;
  created_by: string;
  created_by_name: string | null;
  created_at: string;
}

export interface FundTransactionDocumentCreateResponse {
  id: string;
}

export interface FundTransactionDocumentLinkRequest {
  document_id: string;
  document_types: DocumentType[];
}
